import React from 'react';

/**
 * Formats a timestamp into a relative time string
 * @param timestamp Timestamp to format
 * @returns Formatted string like "Today", "Yesterday", "3d ago", etc.
 */
export const formatRelativeTime = (timestamp: string | number): string => {
  const now = new Date();
  const date = new Date(timestamp);
  
  if (isNaN(date.getTime())) {
    return 'Unknown date';
  }

  const diffTime = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  
  const weeks = Math.floor(diffDays / 7);
  if (weeks < 4) return `${weeks}${weeks === 1 ? 'wk' : 'wks'} ago`;
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Creates a preview of content with highlighted match
 * @param content Full content text
 * @param query Search query to highlight
 * @returns Preview string with ellipses and focused on query match
 */
export const getHighlightedPreview = (content: string, query: string): string => {
  // Find the position of the query in the content (case insensitive)
  const lowerContent = content.toLowerCase();
  const queryPos = lowerContent.indexOf(query.toLowerCase());
  
  if (queryPos === -1) return content.substring(0, 150) + '...';
  
  // Determine the start and end positions for the preview
  let startPos = Math.max(0, queryPos - 50);
  let endPos = Math.min(content.length, queryPos + query.length + 50);
  
  // Adjust to avoid cutting words
  if (startPos > 0) {
    const prevSpace = content.lastIndexOf(' ', startPos);
    if (prevSpace !== -1 && startPos - prevSpace < 15) {
      startPos = prevSpace + 1;
    }
  }
  
  if (endPos < content.length) {
    const nextSpace = content.indexOf(' ', endPos);
    if (nextSpace !== -1 && nextSpace - endPos < 15) {
      endPos = nextSpace;
    }
  }
  
  // Create the preview
  let preview = '';
  if (startPos > 0) preview += '...';
  preview += content.substring(startPos, endPos);
  if (endPos < content.length) preview += '...';
  
  return preview;
};

/**
 * Highlights matched text in a string by wrapping it in a styled span
 * @param text Text to search in
 * @param query Query to highlight
 * @returns Array of strings and React elements with highlighted matches
 * @security Safe from HTML injection since it uses React elements, not innerHTML
 */
export const highlightMatch = (text: string, query: string): (string | React.ReactElement)[] => {
  // Handle null/undefined values for both text and query
  if (!query || !text || typeof text !== 'string' || typeof query !== 'string') {
    return [text || ''];
  }
  
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const parts: (string | React.ReactElement)[] = [];
  
  let lastIndex = 0;
  let index = lowerText.indexOf(lowerQuery, lastIndex);
  
  while (index !== -1) {
    // Add the text before the match
    if (index > lastIndex) {
      parts.push(text.substring(lastIndex, index));
    }
    
    // Add the highlighted match
    parts.push(
      <span key={index} className="highlight-match">
        {text.substring(index, index + query.length)}
      </span>
    );
    
    lastIndex = index + query.length;
    index = lowerText.indexOf(lowerQuery, lastIndex);
  }
  
  // Add the remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }
  
  return parts;
};